"use client";

// Small "✨ generate with AI" button for admin text fields.
//
// Reads as "thinking" not "broken" while loading — the Sparkles icon
// pulses (opacity + slight scale), no spinner. On error, the message
// surfaces as a tooltip on the next hover/focus and auto-clears after
// a few seconds so the button can be tried again. The actual request
// is left to the caller via a `request` prop so the admin app can
// thread its own credentialled fetch / api client through.

import { Sparkles } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { cn } from "../cn.ts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip.tsx";

// Mirror @bloom/ai-fill's FieldKind here so the UI package doesn't have
// to take a dependency on @bloom/ai-fill. Keep these in sync if the
// FieldKind union ever grows.
export type AiFieldKind =
  | "series.title"
  | "series.tagline"
  | "series.synopsis"
  | "episode.title"
  | "episode.description"
  | "cast.role"
  | "legal.section";

export interface AiFieldContext {
  [k: string]: unknown;
}

export interface AiAssistResult {
  en: string;
  th: string;
}

export interface AiAssistButtonProps {
  field: AiFieldKind;
  context: AiFieldContext;
  onResult: (result: AiAssistResult) => void;
  /**
   * How to POST to the generate-field endpoint. The admin app supplies
   * its credentialled `api.post` here; the UI package stays agnostic of
   * fetch wiring so this primitive is reusable.
   */
  request: (body: { field: AiFieldKind; context: AiFieldContext }) => Promise<AiAssistResult>;
  className?: string;
  /** Optional aria-label override. Default: "Generate with AI". */
  label?: string;
}

export const AiAssistButton = forwardRef<HTMLButtonElement, AiAssistButtonProps>(
  function AiAssistButton(
    { field, context, onResult, request, className, label = "Generate with AI" },
    ref,
  ) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Auto-clear the error tooltip after a few seconds so a transient
    // model hiccup doesn't permanently shadow the button.
    useEffect(() => {
      if (!error) return;
      const t = window.setTimeout(() => setError(null), 4_000);
      return () => window.clearTimeout(t);
    }, [error]);

    const onClick = useCallback(async () => {
      if (loading) return;
      setLoading(true);
      setError(null);
      try {
        const result = await request({ field, context });
        onResult(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Generation failed";
        setError(message);
      } finally {
        setLoading(false);
      }
    }, [loading, request, field, context, onResult]);

    const button = (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-busy={loading}
        disabled={loading}
        onClick={onClick}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--brand-rose)] transition-colors",
          "hover:bg-[var(--brand-pink-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose focus-visible:ring-offset-1",
          "disabled:opacity-70",
          error && !loading && "text-red-500",
          className,
        )}
      >
        <Sparkles
          className={cn("h-4 w-4 transition-opacity", loading && "animate-pulse")}
          aria-hidden
        />
      </button>
    );

    if (!error) return button;

    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip open>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] bg-red-600 text-white">
            {error}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
